import reactUpdate from 'immutability-helper';
import {useMemo, useEffect} from 'react';
import isEqual from 'react-fast-compare';

import {buildReactionFromComponents, BuildReactionFromComponentsResponse} from 'chemistry/ChemistryDAO';
import {ChemistryReactionRole} from 'notebook/integrations/chemistry/ChemistryStructuredTableConstants';
import {
  getColumnIndices,
  getMoleculeIdFromEvaluatedCell,
  getRoleFromEvaluatedCell,
} from 'notebook/integrations/chemistry/reaction-scheme/StoichiometryTableHelpers';
import {
  StoichiometryStructuredTable,
  SyncedReactionComponents,
} from 'notebook/integrations/chemistry/StoichiometryStructuredTableAdapter';
import {StructuredTableColumn} from 'notebook/structured-tables/StructuredTableConstants';
import {EvaluatedTable} from 'notebook/tables/excess/Evaluator';
import useAsyncState, {AsyncStatus} from 'ui-library/hooks/useAsyncState';

function extractCurrentTableComponents(
  computedTable: EvaluatedTable,
  columns: Array<StructuredTableColumn>
): SyncedReactionComponents {
  const {compoundColumnIndex, roleColumnIndex} = getColumnIndices(columns);
  const components: SyncedReactionComponents = {
    [ChemistryReactionRole.REACTANT]: [],
    [ChemistryReactionRole.PRODUCT]: [],
  };

  for (const evaluatedRow of computedTable) {
    const moleculeId = getMoleculeIdFromEvaluatedCell(evaluatedRow[compoundColumnIndex]);
    const role = getRoleFromEvaluatedCell(evaluatedRow[roleColumnIndex]);

    if (moleculeId && (role === ChemistryReactionRole.REACTANT || role === ChemistryReactionRole.PRODUCT)) {
      components[role].push(moleculeId);
    }
  }

  return components;
}

function buildUpdatedReactionDiagram(
  components: SyncedReactionComponents
): Promise<BuildReactionFromComponentsResponse> {
  return buildReactionFromComponents(
    components[ChemistryReactionRole.REACTANT],
    components[ChemistryReactionRole.PRODUCT]
  );
}

/**
 * Syncs reaction components from a stoichiometry table to a reaction diagram.
 *
 * Monitors reactant and product molecules in the table and automatically updates
 * the reaction diagram when components change. Calls the chemistry service to build
 * a new reaction diagram and updates the table metadata with the resulting external file.
 *
 * @param structuredTable - The stoichiometry table containing reaction components
 * @param updateHandler - Callback invoked with the updated table when a new diagram is generated
 * @returns true if a diagram update is in progress, false otherwise
 */
export default function useSyncReactionComponentsFromTable(
  structuredTable: StoichiometryStructuredTable,
  computedTable: EvaluatedTable,
  updateHandler: (structuredTable: StoichiometryStructuredTable) => void
): boolean {
  const diagramReactionComponents = structuredTable.metadata.syncedComponents;
  const tableReactionComponents = useMemo(
    () => extractCurrentTableComponents(computedTable, structuredTable.columns),
    [computedTable, structuredTable.columns]
  );
  const {currentState: reactionDiagramUpdateResponse, makeCall: callBuildUpdatedReactionDiagram} =
    useAsyncState({makeCall: buildUpdatedReactionDiagram, keepOnlyLatest: true, includeInput: true});

  useEffect(() => {
    if (
      diagramReactionComponents == null ||
      // Use deep equality check since the tableReactionComponents array is not completely stable.
      !isEqual(tableReactionComponents, diagramReactionComponents)
    ) {
      callBuildUpdatedReactionDiagram(tableReactionComponents);
    }
  }, [tableReactionComponents, diagramReactionComponents, callBuildUpdatedReactionDiagram]);

  const updatedReactionDiagramExternalFile =
    reactionDiagramUpdateResponse.status === AsyncStatus.SUCCESS
      ? reactionDiagramUpdateResponse.data.externalFile
      : null;
  const updatedReactionInput =
    reactionDiagramUpdateResponse.status !== AsyncStatus.NOT_FETCHED
      ? reactionDiagramUpdateResponse.input![0]
      : null;

  useEffect(() => {
    if (
      updatedReactionDiagramExternalFile != null &&
      updatedReactionDiagramExternalFile.id !== structuredTable.metadata.reactionMrvExternalFile?.id &&
      // Ensure that this is not a stale file that no longer matches because the reaction was updated via sketcher.
      isEqual(updatedReactionInput, tableReactionComponents)
    ) {
      updateHandler(
        reactUpdate(structuredTable, {
          metadata: {
            $merge: {
              reactionMrvExternalFile: {
                id: updatedReactionDiagramExternalFile.id,
                url: updatedReactionDiagramExternalFile.url,
              },
              syncedComponents: tableReactionComponents,
            },
          },
        })
      );
    }
  }, [
    updatedReactionDiagramExternalFile,
    structuredTable,
    tableReactionComponents,
    updatedReactionInput,
    updateHandler,
  ]);

  return reactionDiagramUpdateResponse.status === AsyncStatus.LOADING;
}
